import { useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ENTERPRISE_CREATIVES_BUFFER_PERCENT_OPTIONS,
  ENTERPRISE_CREATIVES_BUFFER_TYPES_OPTIONS,
} from "../../../../utils/constants";
import {
  ENTERPRISE_INTAKE_FIELD_CREATIVES_BUFFER,
  ENTERPRISE_INTAKE_FIELD_CREATIVES_BUFFER_PERCENT,
  ENTERPRISE_INTAKE_HINT_CREATIVES_BUFFER,
  ENTERPRISE_INTAKE_HINT_MONTHLY_STATIC_PIECES,
  ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_IMAGE_ANIMATIONS_COUNT,
  ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_NEW_IMAGES_COUNT,
  ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_VIDEOS_COUNT,
  ENTERPRISE_INTAKE_MODULE1_HINT_MONTHLY_IMAGE_ANIMATIONS,
  ENTERPRISE_INTAKE_MODULE1_HINT_MONTHLY_VIDEOS,
  ENTERPRISE_INTAKE_PLACEHOLDER_BUFFER_PERCENT_OTHER,
} from "../../../../i18n/keysTranslations";
import CheckGroup from "../../../PreActivation/components/CheckGroup";
import FieldBlock from "../../../PreActivation/components/FieldBlock";
import FieldError from "../../../PreActivation/components/FieldError";
import FieldHint from "../../../PreActivation/components/FieldHint";
import FieldLabel from "../../../PreActivation/components/FieldLabel";
import ReadOnlySectionGrid from "../../../PreActivation/components/ReadOnlySectionGrid";
import { ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE } from "../../config/enterpriseIntakeModuleFieldRegistry";
import { wantsCreativesBuffer } from "../../enterpriseIntakeFormValues";
import { toggleExclusiveNoneValues } from "../../enterpriseIntakeModuleUtils";
import NumericFieldBlock from "../shared/NumericFieldBlock";
import RadioWithOtherField from "../shared/RadioWithOtherField";
import { mapTranslatedOptions } from "../shared/mapTranslatedOptions";

const CreativeAssetsSection = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();
  const creativesBufferTypes =
    useWatch({ control, name: "creativesBufferTypes" }) ?? [];
  const showBufferPercent = readOnly
    ? wantsCreativesBuffer(creativesBufferTypes) || false
    : wantsCreativesBuffer(creativesBufferTypes);

  const bufferTypeOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_CREATIVES_BUFFER_TYPES_OPTIONS,
        t,
      }),
    [t]
  );

  const bufferPercentOptions = useMemo(
    () =>
      mapTranslatedOptions({
        options: ENTERPRISE_CREATIVES_BUFFER_PERCENT_OPTIONS,
        t,
      }),
    [t]
  );

  return (
    <ReadOnlySectionGrid readOnly={readOnly}>
      <NumericFieldBlock
        name="module1MonthlyNewImagesCount"
        labelKey={ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_NEW_IMAGES_COUNT}
        hintKey={ENTERPRISE_INTAKE_HINT_MONTHLY_STATIC_PIECES}
        required
        control={control}
        errors={errors}
        readOnly={readOnly}
        t={t}
      />

      <NumericFieldBlock
        name="module1MonthlyImageAnimationsCount"
        labelKey={
          ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_IMAGE_ANIMATIONS_COUNT
        }
        hintKey={ENTERPRISE_INTAKE_MODULE1_HINT_MONTHLY_IMAGE_ANIMATIONS}
        required
        control={control}
        errors={errors}
        readOnly={readOnly}
        t={t}
      />

      <NumericFieldBlock
        name="module1MonthlyVideosCount"
        labelKey={ENTERPRISE_INTAKE_MODULE1_FIELD_MONTHLY_VIDEOS_COUNT}
        hintKey={ENTERPRISE_INTAKE_MODULE1_HINT_MONTHLY_VIDEOS}
        required
        control={control}
        errors={errors}
        readOnly={readOnly}
        t={t}
      />

      <FieldBlock>
        <FieldLabel
          htmlFor="creativesBufferTypes"
          text={t(ENTERPRISE_INTAKE_FIELD_CREATIVES_BUFFER)}
          required
        />
        <FieldHint text={t(ENTERPRISE_INTAKE_HINT_CREATIVES_BUFFER)} />
        <Controller
          name="creativesBufferTypes"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <CheckGroup
                name="creativesBufferTypes"
                options={bufferTypeOptions}
                selectedValues={field.value}
                onChange={(nextValues) => {
                  field.onChange(
                    toggleExclusiveNoneValues({
                      previousValues: field.value,
                      nextValues,
                      noneValue: ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE,
                    })
                  );
                }}
                readOnly={readOnly}
              />
              <FieldError message={fieldState.error?.message} />
            </>
          )}
        />
      </FieldBlock>

      {showBufferPercent ? (
        <RadioWithOtherField
          name="creativesBufferPercent"
          otherName="creativesBufferPercentOther"
          labelKey={ENTERPRISE_INTAKE_FIELD_CREATIVES_BUFFER_PERCENT}
          required
          options={bufferPercentOptions}
          control={control}
          errors={errors}
          readOnly={readOnly}
          otherPlaceholderKey={
            ENTERPRISE_INTAKE_PLACEHOLDER_BUFFER_PERCENT_OTHER
          }
        />
      ) : null}
    </ReadOnlySectionGrid>
  );
};

export default CreativeAssetsSection;
